import { defineStore } from "pinia";
import { ENTERPRISE_RELATION_TYPE } from "@supplier/meta";

import type { Customer, MainBody, MemberPermission } from "@supplier/entity";

import { UserInfo, useUserStore } from "@/stores/user";

export enum AccountType {
    COMMON = 1, // 普通账号
    ROOT_ACCOUNT, // 主账号
    SUB_ACCOUNT, // 子账号
    ASSOCIATED // 关联企业
}

export interface AccountStoreState {
    accountType: number;
    actualAccount: Customer;
    actualMemberPermission: MemberPermission;
    proxyActualAccount: Customer;
    proxyMemberPermission: MemberPermission;
    mainBody: MainBody;
}

export interface AccountStoreGetters {
    /**是否是普通账号 */
    isCommonAccount: boolean;
    /**是否是管理员账号 */
    isRootAccount: boolean;
    /**是否是子账号 */
    isSubAccount: boolean;
    /**是否是关联企业 */
    isAssociated: boolean;
    /**账号类型文案 */
    accountTypeDesc: string | undefined;
}

export interface AccountStoreActions {
    setAccountType: () => void;
    setActualAccount: (account: Customer) => void;
    setProxyAccount: (account: Customer) => void;
    setProxyMemberPermission: (memberPermission: MemberPermission) => void;
    setMainBody: (mainBody: MainBody) => void;
}

// 完整的 store 类型
export type AccountStore = AccountStoreState & AccountStoreGetters & AccountStoreActions;

export const useAccountStore = defineStore<
    string, // id 类型
    AccountStoreState, // state
    AccountStoreGetters, // getters
    AccountStoreActions // actions
>({
    id: "zhihao-web-account",
    state: (): AccountStoreState => ({
        accountType: 0,
        actualAccount: {} as Customer,
        actualMemberPermission: {} as MemberPermission,
        proxyActualAccount: {} as Customer,
        proxyMemberPermission: {} as MemberPermission,
        mainBody: {} as MainBody
    }),
    getters: {
        /**是否是普通账号 */
        isCommonAccount: (state: AccountStoreState) => {
            return state.accountType === AccountType.COMMON;
        },
        /**是否是管理员账号 */
        isRootAccount: (state: AccountStoreState) => {
            return state.accountType === AccountType.ROOT_ACCOUNT;
        },
        /**是否是子账号 */
        isSubAccount: (state: AccountStoreState) => {
            return state.accountType === AccountType.SUB_ACCOUNT;
        },
        /**是否是关联企业 */
        isAssociated: (state: AccountStoreState) => {
            return state?.mainBody?.enterpriseRelation === ENTERPRISE_RELATION_TYPE.ASSOCIATED.code;
        },
        /**账号类型文案 */
        accountTypeDesc: (state: AccountStoreState) => {
            const isRootAccount = state.accountType === AccountType.ROOT_ACCOUNT;
            const isSubAccount = state.accountType === AccountType.SUB_ACCOUNT;
            const isAssociated = state?.mainBody?.enterpriseRelation === ENTERPRISE_RELATION_TYPE.ASSOCIATED.code;

            if (isAssociated && !isSubAccount) return "关联企业";
            else if (isSubAccount) return "子账号";
            else if (isRootAccount) {
                return "主账号";
            }
            return undefined;
        }
    },
    actions: {
        setAccountType() {
            const userStore = useUserStore();
            const meVo: UserInfo = userStore.userInfo;
            let accountType = AccountType.COMMON;
            if (meVo?.proxyAccount && meVo?.proxyAccount.id !== meVo?.customer.id) {
                // 如果代理账号信息存在 并且 代理账号客户id != 实际账号客户id
                accountType = AccountType.SUB_ACCOUNT;
            } else if (meVo?.proxyAccount && meVo?.proxyAccount.id === meVo?.customer.id) {
                // 如果代理账号信息不存在 并且 实际账号进行了组织机构认证 就为 主账号
                accountType = AccountType.ROOT_ACCOUNT;
            }
            this.accountType = accountType;
        },
        setActualAccount(account: Customer) {
            this.actualAccount = account;
        },

        setProxyAccount(account: Customer) {
            this.proxyActualAccount = account;
        },
        setProxyMemberPermission(memberPermission: MemberPermission) {
            this.proxyMemberPermission = memberPermission;
        },
        setMainBody(mainBody: MainBody) {
            this.mainBody = mainBody;
        }
    } as AccountStoreActions & ThisType<AccountStore>,
    persist: false
});
